import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { theme } from '../theme';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { useNotification } from './Notification';
import { useWebPush } from '../hooks/useWebPush';
import { useThemeMode, ThemeMode } from '../contexts/ThemeContext';
import { AppHeader } from './AppHeader';
import { DeleteAccountModal } from './DeleteAccountModal';

interface NotificationPreferences {
  matchReminders: boolean;
  playerJoinLeave: boolean;
  matchFullAvailable: boolean;
  reviewsReceived: boolean;
  reviewReminders: boolean;
  statsReminders: boolean;
  newBadges: boolean;
  nearbyMatches: boolean;
  friendsCreateMatches: boolean;
  matchesNeedPlayers: boolean;
}

const DEFAULT_PREFS: NotificationPreferences = {
  matchReminders: true,
  playerJoinLeave: true,
  matchFullAvailable: true,
  reviewsReceived: true,
  reviewReminders: true,
  statsReminders: true,
  newBadges: true,
  nearbyMatches: false,
  friendsCreateMatches: false,
  matchesNeedPlayers: false,
};

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

interface ToggleRowProps {
  title: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ title, description, value, onChange, disabled }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '12px 4px',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>{title}</div>
        <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
          {description}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        style={{
          flexShrink: 0,
          width: 44,
          height: 26,
          padding: 2,
          borderRadius: 999,
          border: 'none',
          background: value ? theme.colors.primary : theme.colors.border,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'background 0.15s ease',
          position: 'relative',
        }}
      >
        <span
          style={{
            display: 'block',
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#fff',
            transform: value ? 'translateX(18px)' : 'translateX(0)',
            transition: 'transform 0.15s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
      </button>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <h3
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        margin: '0 0 10px 4px',
      }}
    >
      {title}
    </h3>
    <div
      style={{
        background: theme.colors.surface,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 14,
        padding: '8px 16px',
      }}
    >
      {children}
    </div>
  </div>
);

const Divider: React.FC = () => (
  <div style={{ height: 1, background: theme.colors.border, margin: '4px -16px' }} />
);

const GroupHeader: React.FC<{ emoji: string; title: string }> = ({ emoji, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 0 4px',
    }}
  >
    <span style={{ fontSize: 18 }}>{emoji}</span>
    <h4
      style={{
        margin: 0,
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.text,
      }}
    >
      {title}
    </h4>
  </div>
);

const ThemeModeSelector: React.FC = () => {
  const { mode, setMode } = useThemeMode();
  const options: { value: ThemeMode; label: string }[] = [
    { value: 'dark', label: 'Oscuro' },
    { value: 'light', label: 'Claro' },
    { value: 'system', label: 'Sistema' },
  ];
  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
        Tema
      </div>
      <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginBottom: 10 }}>
        Elegí cómo se ve la app
      </div>
      <div
        role="radiogroup"
        style={{
          display: 'inline-flex',
          background: theme.colors.background,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: 999,
          padding: 2,
          gap: 0,
        }}
      >
        {options.map((opt) => {
          const active = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setMode(opt.value)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                background: active ? theme.colors.primary : 'transparent',
                color: active ? theme.colors.background : theme.colors.textSecondary,
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
                letterSpacing: 'normal',
                boxShadow: 'none',
                fontFamily: 'inherit',
                textTransform: 'none',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const WebPushToggle: React.FC = () => {
  const { supported, permission, isSubscribed, loading, error, subscribe, unsubscribe } =
    useWebPush();

  const description = !supported
    ? 'Tu navegador no soporta notificaciones push'
    : permission === 'denied'
    ? 'Permiso bloqueado — activalo en la configuración del navegador'
    : isSubscribed
    ? 'Te avisamos en este navegador aunque la app esté cerrada'
    : 'Recibí avisos en este navegador aunque la app esté cerrada';

  const disabled = !supported || permission === 'denied' || loading;

  const handleChange = (next: boolean) => {
    if (next) subscribe();
    else unsubscribe();
  };

  return (
    <>
      <ToggleRow
        title="Notificaciones push"
        description={description}
        value={isSubscribed}
        onChange={handleChange}
        disabled={disabled}
      />
      {error && (
        <div style={{ fontSize: 12, color: theme.colors.danger, padding: '0 4px 8px' }}>{error}</div>
      )}
    </>
  );
};

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { isMobile } = useResponsive();

  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [zoneAlert, setZoneAlert] = useState<{ radiusKm: number } | null>(null);
  const [zoneAlertBusy, setZoneAlertBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetch(getApiUrl('/api/matches/public/my-zone-alert'), { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setZoneAlert(data?.alert ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  const disableZoneAlert = async () => {
    if (zoneAlertBusy) return;
    setZoneAlertBusy(true);
    try {
      const res = await fetch(getApiUrl('/api/matches/public/my-zone-alert'), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) setZoneAlert(null);
    } finally {
      setZoneAlertBusy(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoadingPrefs(true);
    fetch(getApiUrl(`/api/users/${user.id}/notification-preferences`), {
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.preferences) return;
        const p = data.preferences;
        setPrefs({
          matchReminders: p.matchReminders ?? true,
          playerJoinLeave: p.playerJoinLeave ?? true,
          matchFullAvailable: p.matchFullAvailable ?? true,
          reviewsReceived: p.reviewsReceived ?? true,
          reviewReminders: p.reviewReminders ?? true,
          statsReminders: p.statsReminders ?? true,
          newBadges: p.newBadges ?? true,
          nearbyMatches: p.nearbyMatches ?? false,
          friendsCreateMatches: p.friendsCreateMatches ?? false,
          matchesNeedPlayers: p.matchesNeedPlayers ?? false,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingPrefs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const updatePref = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!user?.id) return;
    const previous = prefs[key];
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaving(true);
    try {
      const res = await fetch(getApiUrl(`/api/users/${user.id}/notification-preferences`), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) {
        setPrefs((p) => ({ ...p, [key]: previous }));
        showNotification('No se pudo actualizar la preferencia', 'error');
      }
    } catch {
      setPrefs((p) => ({ ...p, [key]: previous }));
      showNotification('Error de conexión', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('¿Cerrar sesión?')) {
      logout();
      navigate('/auth');
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const res = await fetch(getApiUrl('/api/auth/verify-email'), {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        showNotification('Te enviamos un email de verificación', 'success');
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo enviar el email', 'error');
      }
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const res = await fetch(getApiUrl('/api/users/me/account'), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        showNotification('Cuenta eliminada', 'success');
        localStorage.removeItem('authToken');
        setTimeout(() => navigate('/auth'), 1200);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo eliminar la cuenta', 'error');
      }
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.background,
        padding: isMobile ? '70px 16px 40px' : '20px 40px 40px',
      }}
    >
      <AppHeader title="Configuración" showBackButton />

      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Section title="Apariencia">
          <ThemeModeSelector />
        </Section>

        <Section title="Notificaciones">
          {loadingPrefs ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 32,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  border: '3px solid transparent',
                  borderTop: `3px solid ${theme.colors.primary}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
            </div>
          ) : (
            <>
              <GroupHeader emoji="🔔" title="Navegador" />
              <WebPushToggle />
              <Divider />

              <div style={{ height: 12 }} />
              <GroupHeader emoji="⚽" title="Mis partidos" />
              <ToggleRow
                title="Recordatorios"
                description="24h y 2h antes del partido"
                value={prefs.matchReminders}
                onChange={(v) => updatePref('matchReminders', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Cambios de jugadores"
                description="Alguien se une o se baja"
                value={prefs.playerJoinLeave}
                onChange={(v) => updatePref('playerJoinLeave', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Disponibilidad"
                description="Partido lleno o vuelve a haber lugar"
                value={prefs.matchFullAvailable}
                onChange={(v) => updatePref('matchFullAvailable', v)}
                disabled={saving}
              />

              <div style={{ height: 12 }} />
              <GroupHeader emoji="👥" title="Social" />
              <ToggleRow
                title="Reviews recibidas"
                description="Cuando alguien te deja una review"
                value={prefs.reviewsReceived}
                onChange={(v) => updatePref('reviewsReceived', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Recordatorio de reviews"
                description="Partidos finalizados sin reviews"
                value={prefs.reviewReminders}
                onChange={(v) => updatePref('reviewReminders', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Recordatorio de stats"
                description="Reportar goles y asistencias"
                value={prefs.statsReminders}
                onChange={(v) => updatePref('statsReminders', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Nuevos badges"
                description="Cuando desbloqueás un logro"
                value={prefs.newBadges}
                onChange={(v) => updatePref('newBadges', v)}
                disabled={saving}
              />

              <div style={{ height: 12 }} />
              <GroupHeader emoji="🔍" title="Descubrimiento" />
              <ToggleRow
                title="Partidos cerca"
                description="Partidos públicos cerca de tu zona"
                value={prefs.nearbyMatches}
                onChange={(v) => updatePref('nearbyMatches', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Amigos crean partido"
                description="Cuando alguien de tu lista crea un partido"
                value={prefs.friendsCreateMatches}
                onChange={(v) => updatePref('friendsCreateMatches', v)}
                disabled={saving}
              />
              <Divider />
              <ToggleRow
                title="Partidos necesitan jugadores"
                description="Partidos con lugares disponibles cerca vos"
                value={prefs.matchesNeedPlayers}
                onChange={(v) => updatePref('matchesNeedPlayers', v)}
                disabled={saving}
              />
              {zoneAlert && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: `1px solid ${theme.colors.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: theme.colors.text, fontWeight: 600, fontSize: 14 }}>Aviso de zona</div>
                    <div style={{ color: theme.colors.textSecondary, fontSize: 12 }}>
                      Activado · radio {zoneAlert.radiusKm} km. Te avisamos de partidos públicos nuevos en tu zona.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={disableZoneAlert}
                    disabled={zoneAlertBusy}
                    style={{
                      padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      border: `1px solid ${theme.colors.danger}`, background: 'transparent',
                      color: theme.colors.danger, cursor: zoneAlertBusy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {zoneAlertBusy ? '...' : 'Apagar'}
                  </button>
                </div>
              )}
            </>
          )}
        </Section>

        {user && !user.emailVerified && (
          <Section title="Email">
            <div style={{ padding: '12px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                    Tu email no está verificado
                  </div>
                  <div style={{ fontSize: 12.5, color: theme.colors.textSecondary, marginTop: 2, wordBreak: 'break-all' }}>
                    {user.email}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingVerification}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: theme.colors.primary,
                  color: '#000',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: resendingVerification ? 'not-allowed' : 'pointer',
                  opacity: resendingVerification ? 0.7 : 1,
                }}
              >
                {resendingVerification ? 'Enviando…' : '✉️ Reenviar email de verificación'}
              </button>
            </div>
          </Section>
        )}

        <Section title="Cuenta">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0' }}>
            <button
              type="button"
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 16px',
                background: 'transparent',
                color: theme.colors.danger,
                border: `2px solid ${theme.colors.danger}`,
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              🚪 Cerrar sesión
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 16px',
                background: 'transparent',
                color: theme.colors.textSecondary,
                border: `2px solid ${theme.colors.border}`,
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              🗑 Eliminar cuenta
            </button>
          </div>
        </Section>
      </div>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
};

export default SettingsPage;
